/**
 * Gate for the search-positions panel while it is under review: the signed-in
 * email is on our own domain AND this browser switched it on from the left
 * menu. localStorage like pro mode, not a column anywhere. Pure, so pinned in node.
 */

export const TOPINIT_DOMAIN = "topinit.com";
export const KW_PANEL_FLAG_KEY = "kw-panel-flag";

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** One literal domain, judged on the part after the LAST `@`, case-insensitive. */
export function isTopinitEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const trimmed = email.trim();
  const at = trimmed.lastIndexOf("@");
  if (at < 0) return false;
  return trimmed.slice(at + 1).toLowerCase() === TOPINIT_DOMAIN;
}

/** Only the literal `'1'` counts as on; a missing key or a throwing storage is off. */
export function readKwPanelFlag(storage: StorageLike | null | undefined): boolean {
  try {
    return storage?.getItem(KW_PANEL_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

/** Flips the stored flag and returns the new state. Off removes the key rather than writing `'0'`. */
export function toggleKwPanelFlag(storage: StorageLike): boolean {
  const next = !readKwPanelFlag(storage);
  try {
    if (next) storage.setItem(KW_PANEL_FLAG_KEY, "1");
    else storage.removeItem(KW_PANEL_FLAG_KEY);
  } catch {}
  return next;
}

export function kwPanelVisible(email: string | null | undefined, on: boolean): boolean {
  return on && isTopinitEmail(email);
}

/**
 * What a gated page does on this render. `wait` until storage has been read AND
 * auth has settled, or a topinit user whose flag is on would be sent away on the
 * first commit, before `on` and `user.email` arrive.
 */
export function kwGateState(ready: boolean, authLoading: boolean, visible: boolean): "wait" | "show" | "leave" {
  if (!ready || authLoading) return "wait";
  return visible ? "show" : "leave";
}
